import { ImageModel } from '@/types/models';
import { privateApi } from './api';
import { getToken } from './auth.service';
import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

const getImages = async ({
  projectId,
  sampleId,
}: {
  projectId: string;
  sampleId: string;
}) => {
  return await privateApi.get<ImageModel[]>(
    `/project/${projectId}/sample/${sampleId}/image`,
  );
};

const getViewerImageUrl = async ({
  projectId,
  sampleId,
  imageId,
}: {
  projectId: string;
  sampleId: string;
  imageId: string;
}) => {
  const { data } = await privateApi.get<{ imageUrl: string | null }>(
    `/project/${projectId}/sample/${sampleId}/image/${imageId}/viewerImageUrl`,
  );
  return data.imageUrl;
};

export const getViewerThumbnailUrl = async ({
  projectId,
  sampleId,
  imageId,
}: {
  projectId: string;
  sampleId: string;
  imageId: string;
}) => {
  const { data } = await privateApi.get<{ imageUrl: string }>(
    `/project/${projectId}/sample/${sampleId}/image/${imageId}/viewerThumbnailUrl`,
  );
  return data.imageUrl;
};

/** `size` is the object's size in bytes, and is only set when it is in S3. */
export type IntegrityAcquisitionFile = {
  name: string;
  inS3: boolean;
  size?: number;
};

export type IntegrityAcquisition = {
  acquisitionDate?: string;
  imageId?: string;
  jp2Name: string;
  inDatabase: boolean;
  inS3: boolean;
  filesInS3: number;
  jp2?: IntegrityAcquisitionFile;
  thumb?: IntegrityAcquisitionFile;
  json?: IntegrityAcquisitionFile;
};

export type IntegrityProjectSummary = {
  projectId: string;
  projectName: string;
};

export type IntegritySampleSummary = {
  sampleId: string;
  sampleName: string;
  /** The scanner feeding the sample, if any. */
  scannerSerialNumber: string | null;
};

const getIntegrityProjects = async () => {
  const { data } = await privateApi.get<IntegrityProjectSummary[]>(
    '/image/integrity/projects',
  );
  return data;
};

const getIntegritySamples = async (projectId: string) => {
  const { data } = await privateApi.get<IntegritySampleSummary[]>(
    `/image/integrity/projects/${projectId}/samples`,
  );
  return data;
};

const getIntegrityAcquisitions = async (
  projectId: string,
  sampleId: string,
) => {
  const { data } = await privateApi.get<IntegrityAcquisition[]>(
    `/image/integrity/projects/${projectId}/samples/${sampleId}/acquisitions`,
  );
  return data;
};

const deleteImageFromDatabase = async (imageName: string) => {
  try {
    await privateApi.delete(
      `/image/integrity/database/${encodeURIComponent(imageName)}`,
    );
  } catch (error) {
    console.error('Error deleting image from database:', error);
    throw error;
  }
};

const deleteImageFromS3 = async (imageName: string) => {
  try {
    await privateApi.delete(
      `/image/integrity/s3/${encodeURIComponent(imageName)}`,
    );
  } catch (error) {
    console.error('Error deleting image from S3:', error);
    throw error;
  }
};

const deleteImagesFromDatabase = async (names: string[]) => {
  const { data } = await privateApi.post<{
    deleted: number;
    failures: { name: string; error: string }[];
  }>(`/image/integrity/delete-many-from-database`, { names });
  return data;
};

const deleteImagesFromS3 = async (names: string[]) => {
  const { data } = await privateApi.post<{
    deleted: number;
    failures: { name: string; error: string }[];
  }>(`/image/integrity/delete-many-from-s3`, { names });
  return data;
};

export type MaskUrls = { roots: string | null; gallery: string | null };

const getMaskUrls = async ({
  projectId,
  imageId,
}: {
  projectId: string;
  imageId: string;
}): Promise<MaskUrls> => {
  try {
    const { data } = await privateApi.get<MaskUrls>(
      `/project/${projectId}/image/${imageId}/mask/url`,
    );
    return { roots: data?.roots ?? null, gallery: data?.gallery ?? null };
  } catch {
    // No mask for this image is a normal case, not an error.
    return { roots: null, gallery: null };
  }
};

type SampleComputeItem = {
  projectName: string;
  sampleName: string;
  sampleId: string;
  missingImages: number;
  totalImages: number;
};

const getSampleComputeStatus = async () => {
  try {
    const { data } =
      await privateApi.get<SampleComputeItem[]>(`/image/computeStatus`);
    return data;
  } catch (error) {
    console.error('Error fetching sample compute status:', error);
    throw error;
  }
};

const computeSoilHumidityForMissingImages = async ({
  sampleId,
}: {
  sampleId: string;
}) => {
  return await privateApi.post(
    `/image/${sampleId}/computeSoilHumidityForMissingImages`,
  );
};

const recomputeSoilHumidityForAllImages = async ({
  sampleId,
}: {
  sampleId: string;
}) => {
  return await privateApi.post(
    `/image/${sampleId}/recomputeSoilHumidityForAllImages`,
  );
};

const computeRootDetectionForMissingImages = async ({
  sampleId,
}: {
  sampleId: string;
}) => {
  return await privateApi.post(
    `/image/${sampleId}/computeRootDetectionForMissingImages`,
  );
};

const recomputeRootDetectionForAllImages = async ({
  sampleId,
}: {
  sampleId: string;
}) => {
  return await privateApi.post(
    `/image/${sampleId}/recomputeRootDetectionForAllImages`,
  );
};

const computeRootInferenceForMissingImages = async ({
  sampleId,
}: {
  sampleId: string;
}) => {
  return await privateApi.post(
    `/image/${sampleId}/computeRootInferenceForMissingImages`,
  );
};

const recomputeRootInferenceForAllImages = async ({
  sampleId,
}: {
  sampleId: string;
}) => {
  return await privateApi.post(
    `/image/${sampleId}/recomputeRootInferenceForAllImages`,
  );
};

const updateImageAcquisitionDate = async ({
  imageId,
  acquisitionDate,
}: {
  imageId: string;
  acquisitionDate: string;
}) => {
  return await privateApi.patch(`/image/${imageId}/acquisition-date`, {
    acquisitionDateString: new Date(acquisitionDate).toISOString(),
  });
};

const moveImagesToSample = async ({
  imageIds,
  sampleId,
}: {
  imageIds: string[];
  sampleId: string;
}) => {
  const { data } = await privateApi.post<{
    moved: number;
    failures: { imageId: string; error: string }[];
  }>(`/image/move-many-to-sample/${sampleId}`, { imageIds });
  return data;
};

// Hooks
export const useImages = (projectId?: string, sampleId?: string) => {
  return useQuery({
    queryKey: ['images', projectId, sampleId],
    queryFn: () => {
      if (!projectId || !sampleId)
        return Promise.reject('Project ID or Sample ID missing');
      const response = getImages({
        projectId: projectId!,
        sampleId: sampleId!,
      });
      return response.then((response) => response.data) || [];
    },
    enabled: !!projectId && !!sampleId,
  });
};

/** useImages for several samples at once, sharing its cache. */
export const useImagesOfSamples = (projectId: string, sampleIds: string[]) =>
  useQueries({
    queries: sampleIds.map((sampleId) => ({
      queryKey: ['images', projectId, sampleId],
      queryFn: () =>
        getImages({ projectId, sampleId }).then((response) => response.data),
    })),
  });

/**
 * The IIIF info.json URL for an image. It is derived from the S3 key, so for a
 * given image it is always the same string — but the backend does a HeadObject
 * against S3 on every call to check the jp2 has actually landed, so refetching
 * it is a round trip that can only ever return what we already have.
 *
 * Cached for five minutes rather than forever: the jp2 arrives after the
 * thumbnail for a freshly scanned acquisition, and a null answer has to be
 * allowed to become non-null without a reload.
 */
const VIEWER_URL_STALE_MS = 5 * 60 * 1000;

/**
 * Presigned thumbnail URLs. The backend signs these for an hour (see
 * getViewerThumbnailUrl), so the cache has to expire comfortably before that
 * or a URL held across a long session starts coming back 403.
 */
const THUMB_URL_STALE_MS = 45 * 60 * 1000;

export const useViewerImageUrl = (
  projectId?: string,
  sampleId?: string,
  imageId?: string,
) => {
  return useQuery({
    queryKey: ['viewerImageUrl', projectId, sampleId, imageId],
    queryFn: () => {
      if (!projectId || !sampleId || !imageId)
        return Promise.reject('Project ID, Sample ID or Image ID missing');
      return getViewerImageUrl({
        projectId: projectId!,
        sampleId: sampleId!,
        imageId: imageId!,
      });
    },
    enabled: !!projectId && !!sampleId && !!imageId,
    staleTime: VIEWER_URL_STALE_MS,
    gcTime: VIEWER_URL_STALE_MS,
    // Stepping back and forth through a sample's acquisitions must not re-ask
    // the backend for a URL it already gave us, and tabbing away and back is
    // not new information about where an image lives.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Shared by the hook below and by ImageList's prefetch. They must agree on the
 * key *and* on staleTime: a prefetch that omits it inherits the client default
 * of 0, so every prefetch re-signs URLs the cache already holds, which is
 * exactly the request storm you get while stepping through a sample.
 */
export const viewerThumbnailQuery = (
  projectId: string,
  sampleId: string,
  imageId: string,
) => ({
  queryKey: ['viewerThumbnailUrl', projectId, sampleId, imageId] as const,
  queryFn: () => getViewerThumbnailUrl({ projectId, sampleId, imageId }),
  staleTime: THUMB_URL_STALE_MS,
  gcTime: THUMB_URL_STALE_MS,
});

export const useViewerThumbnailUrl = (
  projectId?: string,
  sampleId?: string,
  imageId?: string,
) => {
  return useQuery({
    ...viewerThumbnailQuery(projectId!, sampleId!, imageId!),
    enabled: !!projectId && !!sampleId && !!imageId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useIntegrityProjects = () => {
  return useQuery({
    queryKey: ['integrity', 'projects'],
    queryFn: getIntegrityProjects,
    enabled: !!getToken(),
  });
};

export const useIntegritySamples = (projectId?: string) => {
  return useQuery({
    queryKey: ['integrity', 'samples', projectId],
    queryFn: () => getIntegritySamples(projectId!),
    enabled: !!projectId,
  });
};

export const useIntegrityAcquisitions = (
  projectId?: string,
  sampleId?: string,
) => {
  return useQuery({
    queryKey: ['integrity', 'acquisitions', projectId, sampleId],
    queryFn: () => getIntegrityAcquisitions(projectId!, sampleId!),
    enabled: !!projectId && !!sampleId,
  });
};

export const useDeleteImageFromDatabase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteImageFromDatabase,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrity', 'acquisitions'],
      });
    },
    onError: (error) => {
      console.error('Error while deleting image from database', error);
      throw error;
    },
  });
};

export const useDeleteImageFromS3 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteImageFromS3,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrity', 'acquisitions'],
      });
    },
    onError: (error) => {
      console.error('Error while deleting image from S3', error);
      throw error;
    },
  });
};

export const useDeleteImagesFromDatabase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteImagesFromDatabase,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrity', 'acquisitions'],
      });
    },
    onError: (error) => {
      console.error('Error while bulk deleting from database', error);
      throw error;
    },
  });
};

export const useDeleteImagesFromS3 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteImagesFromS3,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrity', 'acquisitions'],
      });
    },
    onError: (error) => {
      console.error('Error while bulk deleting from S3', error);
      throw error;
    },
  });
};

export const useMaskUrls = (projectId?: string, imageId?: string) => {
  return useQuery({
    queryKey: ['maskUrls', projectId, imageId],
    queryFn: () => {
      if (!projectId || !imageId)
        return Promise.reject('Project ID or Image ID missing');
      return getMaskUrls({ projectId: projectId!, imageId: imageId! });
    },
    enabled: !!projectId && !!imageId,
    retry: 1,
  });
};

export const useSampleComputeStatus = () => {
  return useQuery({
    queryKey: ['sampleComputeStatus'],
    queryFn: getSampleComputeStatus,
    enabled: !!getToken(),
  });
};

// Batch jobs touch many images across many samples; we don't know exactly
// which from the mutation variables, so invalidate by prefix to cover
// every cached image list + the backoffice compute-status table.
const invalidateBatchComputeQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['images'] });
  queryClient.invalidateQueries({ queryKey: ['sampleComputeStatus'] });
};

export const useComputeSoilHumidityForMissingImages = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: computeSoilHumidityForMissingImages,
    onSuccess: () => invalidateBatchComputeQueries(queryClient),
    onError: (error) => {
      console.error('Error computing soil humidity for missing images:', error);
    },
  });
};

export const useRecomputeSoilHumidityForAllImages = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recomputeSoilHumidityForAllImages,
    onSuccess: () => invalidateBatchComputeQueries(queryClient),
    onError: (error) => {
      console.error('Error recomputing soil humidity for all images:', error);
    },
  });
};

export const useComputeRootDetectionForMissingImages = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: computeRootDetectionForMissingImages,
    onSuccess: () => invalidateBatchComputeQueries(queryClient),
    onError: (error) => {
      console.error(
        'Error computing root detection for missing images:',
        error,
      );
    },
  });
};

export const useRecomputeRootDetectionForAllImages = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recomputeRootDetectionForAllImages,
    onSuccess: () => invalidateBatchComputeQueries(queryClient),
    onError: (error) => {
      console.error('Error recomputing root detection for all images:', error);
    },
  });
};

export const useComputeRootInferenceForMissingImages = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: computeRootInferenceForMissingImages,
    onSuccess: () => invalidateBatchComputeQueries(queryClient),
    onError: (error) => {
      console.error(
        'Error computing root inference for missing images:',
        error,
      );
    },
  });
};

export const useRecomputeRootInferenceForAllImages = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recomputeRootInferenceForAllImages,
    onSuccess: () => invalidateBatchComputeQueries(queryClient),
    onError: (error) => {
      console.error('Error recomputing root inference for all images:', error);
    },
  });
};

export const useUpdateImageAcquisitionDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateImageAcquisitionDate,
    onSuccess: () => {
      // The acquisition date is part of the S3 key, so a change renames the
      // files too — refresh the integrity view (and any image lists keyed off
      // the date) to reflect the new names/dates.
      queryClient.invalidateQueries({
        queryKey: ['integrity', 'acquisitions'],
      });
      queryClient.invalidateQueries({ queryKey: ['images'] });
      // The date is also the curve point's x value, which was re-dated with it.
      queryClient.invalidateQueries({ queryKey: ['sampleGraphs'] });
    },
    onError: (error) => {
      console.error('Error while updating acquisition date', error);
      throw error;
    },
  });
};

export const useMoveImagesToSample = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: moveImagesToSample,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrity', 'acquisitions'],
      });
      // The acquisition's curve point moves with it, so the source and target
      // samples' graphs both changed.
      queryClient.invalidateQueries({ queryKey: ['sampleGraphs'] });
      queryClient.invalidateQueries({ queryKey: ['images'] });
    },
    onError: (error) => {
      console.error('Error while moving images to sample', error);
      throw error;
    },
  });
};
